import React, { FC, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import remove from 'lodash/remove';
import Paper from '@material-ui/core/Paper';
import {
  fetchSerialNumbers,
  getSerialNumbers,
  resetSerialNumbersList,
  getSerialNumberGroups,
  getSerialNumbersPagination,
  getSerialNumbersLoaders,
  SerialNumberGroup
} from 'features/SerialNumbers/serialNumbers';
import { mainColor } from 'theme/Theme';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import moment from 'moment';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import { PageIndex } from 'components/UI/PageIndex';
import { TableLoader } from 'components/Loaders/TableLoader';
import { Row } from 'components/UI/Row';

interface SelectableSerialNumbersProps {
  product: any;
  selectedSerialNumbers: any;
  setSelectedSerialNumbers: (serialNumber: any) => void;
}

const SelectableSerialNumbers: FC<SelectableSerialNumbersProps> = ({
  product,
  selectedSerialNumbers,
  setSelectedSerialNumbers
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orderListId } = useParams<{ orderListId?: string }>();
  const { isLoading } = useSelector(getSerialNumbersLoaders);
  const serialNumbers = useSelector(getSerialNumbers);
  const pagination = useSelector(getSerialNumbersPagination);
  const serialNumberGroups = useSelector(getSerialNumberGroups);
  const tableRef = useRef<HTMLTableElement>(null);
  const sectionRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (sectionRef) {
        sectionRef?.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 250);
  }, [product]);

  useEffect(() => {
    dispatch(
      fetchSerialNumbers({
        productId: product.id,
        siteId: 'all',
        only_summary: true,
        current_order_list: orderListId || 'any',
        queryIndex: 1
      })
    );
    return () => {
      dispatch(resetSerialNumbersList());
    };
  }, []);

  const isSelected = useCallback((sn: string) => selectedSerialNumbers.find((e: string) => e === sn), [
    selectedSerialNumbers
  ]);

  const handleSelection = useCallback(
    (sn: string) => {
      const updated = [...selectedSerialNumbers];
      if (isSelected(sn)) {
        remove(updated, (e: string) => e === sn);
      } else {
        updated.push(sn);
      }
      setSelectedSerialNumbers(updated);
    },
    [isSelected, selectedSerialNumbers, setSelectedSerialNumbers]
  );

  return (
    <>
      <div style={{ margin: '30px 0 20px' }} ref={sectionRef}>
        <strong>Serial Numbers</strong>
      </div>

      <TableContainer component={Paper} elevation={3} style={{ height: '60vh' }}>
        <Table stickyHeader aria-label="order list" ref={tableRef}>
          <TableBody>
            {isLoading && <TableLoader colSpan={1} />}
            {!isLoading && isEmpty(serialNumbers) && (
              <TableRow>
                <TableCell>
                  <Row height="50vh" justifyContent="center" alignItems="center">
                    {t('products.serial numbers no available for order list')}
                  </Row>
                </TableCell>
              </TableRow>
            )}
            {map(serialNumberGroups, (group: SerialNumberGroup) => (
              <React.Fragment key={group.day}>
                <TableRow style={{ backgroundColor: '#f2f2f2' }}>
                  <TableCell>
                    <strong>{moment(group.day).format('ll')}</strong>
                  </TableCell>
                </TableRow>
                {map(group.serial_numbers, sn => (
                  <TableRow
                    hover
                    key={sn.value}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected(sn.value) ? mainColor : ''
                    }}
                  >
                    <TableCell
                      onClick={() => {
                        handleSelection(sn.value);
                      }}
                      style={{ color: isSelected(sn.value) ? 'white' : 'black' }}
                    >
                      {sn.value}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PageIndex
        margin="25px 0"
        justifyContent="center"
        index={pagination.index}
        indexesCount={pagination.indexesCount || 0}
        onChange={async (_, page: number) => {
          await dispatch(
            fetchSerialNumbers({
              productId: product.id,
              siteId: 'all',
              only_summary: true,
              current_order_list: orderListId || 'any',
              queryIndex: page
            })
          );
          if (tableRef) {
            tableRef?.current?.scrollIntoView();
          }
        }}
      />
    </>
  );
};

export { SelectableSerialNumbers };
